import { AppBar, Toolbar, Box, IconButton, Tooltip, Typography } from '@mui/material';
import MenuRounded from '@mui/icons-material/MenuRounded';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import { useThemeMode } from '../../theme/ThemeModeContext';
import UserMenu from './UserMenu';
import type { UserShape } from './useAuthShell';
import HorizontalNav from './HorizontalNav';
import logo from '../../assets/logo.png';

type Props = {
  user: UserShape;
  avatarInitial: string;
  active: string;
  go: (path: string) => void;
  onLogout: () => void;
  onOpenMobileNav: () => void;
};

const Topbar = ({ user, avatarInitial, active, go, onLogout, onOpenMobileNav }: Props) => {
  const { mode, toggleMode } = useThemeMode();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        color: 'text.primary',
        borderRadius: 0,
        border: 0,
        bgcolor: (theme) =>
          theme.palette.mode === 'light' ? 'rgba(255,255,255,0.78)' : 'rgba(23,25,35,0.78)',
        backdropFilter: 'blur(16px) saturate(160%)',
        boxShadow: '0 10px 30px rgba(98,38,167,0.10)',
        '&::after': {
          content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
          background: 'linear-gradient(90deg,#6226A7,#EC4899,#F6A938)',
        },
      }}
    >
      <Toolbar sx={{ minHeight: 64, display: 'grid', gridTemplateColumns: { xs: 'auto 1fr auto', md: 'auto 1fr auto' }, alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            edge="start"
            onClick={onOpenMobileNav}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="Open menu"
          >
            <MenuRounded />
          </IconButton>

          <Box
            onClick={() => go('/dashboard')}
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.25, cursor: 'pointer', '&:hover .qs-logo-img': { transform: 'rotate(-8deg) scale(1.08)' } }}
          >
            <Box
              component="img"
              className="qs-logo-img"
              src={logo}
              alt="QuillStack"
              sx={{ width: 30, height: 30, borderRadius: 1, transition: 'transform .25s ease' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 18, whiteSpace: 'nowrap' }}>QuillStack</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <HorizontalNav active={active} go={go} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifySelf: 'end' }}>
          <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton
              onClick={toggleMode}
              aria-label="Toggle color mode"
              sx={{ transition: 'transform .35s ease', '&:hover': { transform: 'rotate(20deg) scale(1.1)' } }}
            >
              {mode === 'light' ? <DarkModeRounded /> : <LightModeRounded />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton aria-label="Notifications" sx={{ position: 'relative' }}>
              <NotificationsNoneRounded />
              <Box
                sx={{
                  position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: '50%',
                  bgcolor: 'error.main',
                }}
              />
              <Box
                sx={{
                  position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: '50%',
                  bgcolor: 'error.main', animation: 'qsPing 1.6s cubic-bezier(0,0,0.2,1) infinite',
                }}
              />
            </IconButton>
          </Tooltip>

          <UserMenu user={user} avatarInitial={avatarInitial} go={go} onLogout={onLogout} size={34} />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
